/**
 * Account FAQ model — id-keyed EAV collection under account.data.content.faqs
 * (the content-kind convention shared with regions/suburbs/charities/blogs).
 * Storage path: account.data.content.faqs.<id>.{question, answer, pages, order}
 *
 * Authored on the Accounts page (Content Manager+), written via the
 * RBAC-gated PATCH /account/content route (kind 'faqs'), and consumed by
 * picaluna-v3 through GET /external/v1/accounts/:id/faqs → the snapshot's
 * `faqs` array → <FaqOverview>.
 *
 * `pages` is the feature's point: the list of picaluna.com URLs this FAQ
 * renders on — ONE item can appear on SEVERAL pages.
 */

export interface AccountFaq {
  _meta: {
    id: string;
    index: number;
    deleted?: boolean;
  };
  /** The question — also the item's display key. */
  question: string;
  /** Plain-text answer (40–80 words reads best on the site). */
  answer: string;
  /** picaluna.com URLs this FAQ renders on, e.g. ['/', '/cremation']. */
  pages: string[];
  /** Sort position within a page's FAQ section (lower first). */
  order?: number;
  /**
   * The category this FAQ belongs to — an AccountFaqCategory `_meta.id`
   * (#202). Blank means ungrouped.
   *
   * Records written before categories were records hold a SLUG
   * ('costs', 'arranging'). The seed gives each of those a real category
   * record whose id is that same slug, so old values keep resolving and no
   * data migration is needed.
   */
  category?: string;
  /**
   * Show this item in a FULL PAGE FAQ block (the /faq listing). Section
   * blocks name their items explicitly and ignore this.
   *
   * Absent means TRUE: every FAQ predating the toggle was on the hub by
   * virtue of having a category, and defaulting to false would empty the
   * page on deploy.
   */
  onFaqPage?: boolean;
}

/**
 * A FAQ category (#202) — its own record under
 * account.data.content.faqCategories.<id>, so a category has a name, an
 * intro paragraph and a display position instead of being a bare string
 * repeated on every item.
 *
 * Replaces a fixed list that was mirrored in this repo AND in picaluna-v3's
 * faq-hub: adding one meant editing both, and a value missing from v3's
 * order array rendered nothing at all.
 */
export interface AccountFaqCategory {
  _meta: {
    id: string;
    index: number;
    deleted?: boolean;
  };
  /** Heading shown above the group. */
  name: string;
  /** Optional intro paragraph under that heading. */
  description?: string;
  /** Display position on the page (lower first). */
  order?: number;
}

/**
 * The categories the fixed list used to define, seeded as records the first
 * time the list is opened. The id IS the old slug, so FAQs written before
 * this keep resolving with no migration.
 */
export const SEED_FAQ_CATEGORIES: Array<{ id: string; name: string; order: number }> = [
  { id: 'arranging', name: 'Arranging a funeral', order: 10 },
  { id: 'costs', name: 'Costs and payment', order: 20 },
  { id: 'services', name: 'Services and options', order: 30 },
  { id: 'cremation', name: 'Cremation', order: 40 },
  { id: 'personalisation', name: 'Personalisation and cultural needs', order: 50 },
  { id: 'about', name: 'About Picaluna', order: 60 },
  { id: 'locations', name: 'Where we operate', order: 70 },
];

